/**
 * App Chunk Preloader Service
 * 
 * Preloads React.lazy() chunks for authorized apps after user authentication
 * to improve UX by eliminating loading spinners when navigating between apps.
 * 
 * Security: Only preloads chunks for apps the user has access to.
 */

import { AppAccess } from '../types/appAccess';

interface LazyComponent {
  (): Promise<{ default: React.ComponentType<any> }>;
}

/**
 * Mapping of app_id to their lazy-loaded components
 * This must be kept in sync with the lazy imports in App.tsx
 */
const APP_CHUNK_MAP: Record<string, LazyComponent> = {
  'special_flooded_evaporator': () => import(/* webpackChunkName: "special-flooded-evap" */ '../components/special_flooded_evaporator/SpecialFloodedEvaporatorUI'),
  'special_condenser': () => import(/* webpackChunkName: "special-condenser" */ '../components/special_condenser/SpecialCondenserUI'),
  'flooded_evaporator': () => import(/* webpackChunkName: "flooded-evaporator" */ '../components/flooded_evaporator/FloodedEvaporatorUI'),
  'dry_expansion_evaporator': () => import(/* webpackChunkName: "dry-expansion-evap" */ '../components/dry_expansion_evaporator/DryExpansionEvaporatorUI'),
  'condenser': () => import(/* webpackChunkName: "condenser" */ '../components/condenser/CondenserUI'),
  'cold_plate': () => import(/* webpackChunkName: "cold-plate" */ '../components/cold_plate/ColdPlateUI'),
  'coaxial_condenser': () => import(/* webpackChunkName: "coaxial-condenser" */ '../components/coaxial_condenser/CoaxialCondenserUI'),
  'coaxial_evaporator': () => import(/* webpackChunkName: "coaxial-evaporator" */ '../components/coaxial_evaporator/CoaxialEvaporatorUI'),
  'tube_comparison': () => import(/* webpackChunkName: "tube-comparison" */ '../components/tube_comparison/TubeComparisonUI'),
  'special_flooded_evaporator_ai': () => import(/* webpackChunkName: "special-flooded-evap-ai" */ '../components/special_flooded_evaporator_ai/SpecialFloodedEvaporatorAIUI'),
  'expert_app': () => import(/* webpackChunkName: "heat-transfer-expert" */ '../components/heat_transfer_expert/HeatTranferExpertUI'),
  'documents': () => import(/* webpackChunkName: "documents" */ '../pages/DocumentsPage'),
  'share': () => import(/* webpackChunkName: "share" */ '../pages/SharePage'),
  'air_heatexchanger': () => import(/* webpackChunkName: "air-heat-exchanger" */ '../components/air_heatexchanger_internal/AirHeatExchangerInternalUI'),
};

export interface PreloadResult {
  appId: string;
  success: boolean;
  error?: Error;
}

export interface PreloadSummary {
  totalApps: number;
  successfulPreloads: number;
  failedPreloads: number;
  results: PreloadResult[];
  duration: number; // in milliseconds
}

class AppChunkPreloader {
  private preloadedApps = new Set<string>();
  private preloadPromises = new Map<string, Promise<PreloadResult>>();

  /**
   * Preload chunks for all authorized apps
   * @param authorizedApps List of apps the user has access to
   * @returns Promise that resolves with preload summary
   */
  public async preloadAuthorizedApps(authorizedApps: AppAccess[]): Promise<PreloadSummary> {
    const startTime = Date.now();
    
    // Preloading authorized app chunks
    
    // Create preload promises for each authorized app
    const preloadPromises = authorizedApps.map(app => this.preloadApp(app.app_id));
    
    // Wait for all preloads to complete (success or failure)
    const results = await Promise.all(preloadPromises);
    
    const duration = Date.now() - startTime;
    const successfulPreloads = results.filter(r => r.success).length;
    const failedPreloads = results.length - successfulPreloads;
    
    const summary: PreloadSummary = {
      totalApps: authorizedApps.length,
      successfulPreloads,
      failedPreloads,
      results,
      duration
    };
    
    // Preloading completed
    if (failedPreloads > 0) {
      console.error('Some chunks failed to preload:', 
        results.filter(r => !r.success).map(r => ({ app: r.appId, error: r.error?.message }))
      );
    }
    
    return summary;
  }

  /**
   * Preload a single app chunk
   * @param appId The app ID to preload
   * @returns Promise that resolves with preload result
   */
  private async preloadApp(appId: string): Promise<PreloadResult> {
    // Return cached promise if already preloading
    if (this.preloadPromises.has(appId)) {
      return this.preloadPromises.get(appId)!;
    }

    // Return success if already preloaded
    if (this.preloadedApps.has(appId)) {
      return { appId, success: true };
    }

    const preloadPromise = this.executePreload(appId);
    this.preloadPromises.set(appId, preloadPromise);
    
    return preloadPromise;
  }

  /**
   * Execute the actual preload for an app
   */
  private async executePreload(appId: string): Promise<PreloadResult> {
    try {
      const lazyComponent = APP_CHUNK_MAP[appId];
      
      if (!lazyComponent) {
        throw new Error(`No lazy component found for app: ${appId}`);
      }
      

      await lazyComponent();
      
      this.preloadedApps.add(appId);
      return { appId, success: true };
    } catch (error) {
      const errorObj = error instanceof Error ? error : new Error(String(error));
      console.error(`✗ Failed to preload chunk for app: ${appId}`, errorObj);
      
      return { 
        appId, 
        success: false, 
        error: errorObj 
      };
    }
  }

  /**
   * Check if an app has been preloaded
   */
  public isPreloaded(appId: string): boolean {
    return this.preloadedApps.has(appId);
  }

  /**
   * Get list of preloaded app IDs
   */
  public getPreloadedApps(): string[] {
    return Array.from(this.preloadedApps);
  }

  /**
   * Clear preload cache (useful for testing or logout)
   */
  public clearCache(): void {
    this.preloadedApps.clear();
    this.preloadPromises.clear();
  }

  /**
   * Preload specific apps by ID (useful for manual preloading)
   */
  public async preloadSpecificApps(appIds: string[]): Promise<PreloadResult[]> {
    const promises = appIds.map(appId => this.preloadApp(appId));
    return Promise.all(promises);
  }
}

// Export singleton instance
export const appChunkPreloader = new AppChunkPreloader();
export default appChunkPreloader;
